//File imports
import HttpService from '../../../services/Http/HttpService';
import Logger from '../../../services/Logger/Logger';
import Validator from '../../../services/Validator/Validator';
import {
    API_CREATE_DOCUMENT_CATEGORY_PAGE_TRANSLATION,
    API_IS_URL_EXISTS,
    API_UPDATE_DOCUMENT_CATEGORY_PAGE_TRANSLATION,
    API_UPDATE_DOCUMENT_CATEGORY_PAGE_TRANSLATION_STATUS,
    GET_ALL_ACTIVE_DOCUMENT_TEMPLATES,
    GET_SINGLE_DOCUMENT_CATEGORY_PAGE_TRANSLATION,
} from './DocumentCategoryPageTranslationConstants';
import DocumentCategoryPageTranslationResultsPojo from './DocumentCategoryPageTranslationResultsPojo';

export class DocumentCategoryPageTranslationService {
    private httpService = new HttpService();
    private logger = new Logger();
    private validator = new Validator();

    async create(data: any): Promise<{ success: boolean; message: string }> {
        const {
            documentCategoryPageId,
            languageId,
            url,
            metaTitle,
            metaKeyword,
            metaDescription,
            title,
            heading,
            description,
            documentTemplateIds,
        } = data;
        try {
            this.validator.validatePositiveInteger(documentCategoryPageId);
            this.validator.validatePositiveInteger(languageId);
            this.validator.isValidString('url', url);
            this.validator.isValidString('metaTitle', metaTitle);
            this.validator.isValidString('metaKeyword', metaKeyword);
            this.validator.isValidString('metaDescription', metaDescription);
            this.validator.isValidString('title', title);
            this.validator.isValidString('heading', heading);
            this.validator.isValidString('description', description);
            if (documentTemplateIds && documentTemplateIds.length > 0) {
                documentTemplateIds.forEach((id: any) => {
                    this.validator.validatePositiveInteger(id);
                });
            }

            let formData = new FormData();
            formData.append('documentCategoryPageId', documentCategoryPageId);
            formData.append('languageId', languageId);
            formData.append('url', url.toLowerCase());
            formData.append('metaTitle', metaTitle);
            formData.append('metaKeyword', metaKeyword);
            formData.append('metaDescription', metaDescription);
            formData.append('title', title);
            formData.append('heading', heading);
            formData.append('description', description);
            if (documentTemplateIds && documentTemplateIds.length > 0) {
                documentTemplateIds.forEach((id: any) => {
                    formData.append('documentTemplateIds[]', id);
                });
            }

            const httpResult = await this.httpService.postFormData(
                API_CREATE_DOCUMENT_CATEGORY_PAGE_TRANSLATION,
                formData,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async update(data: any): Promise<{ success: boolean; message: string }> {
        const {
            documentCategoryPageTranslationId,
            documentCategoryPageId,
            languageId,
            url,
            metaTitle,
            metaKeyword,
            metaDescription,
            title,
            heading,
            description,
            documentTemplateIds,
        } = data;

        try {
            this.validator.validatePositiveInteger(
                documentCategoryPageTranslationId,
            );
            this.validator.validatePositiveInteger(documentCategoryPageId);
            this.validator.validatePositiveInteger(languageId);
            this.validator.isValidString('url', url);
            this.validator.isValidString('metaTitle', metaTitle);
            this.validator.isValidString('metaKeyword', metaKeyword);
            this.validator.isValidString('metaDescription', metaDescription);
            this.validator.isValidString('title', title);
            this.validator.isValidString('heading', heading);
            this.validator.isValidString('description', description);
            if (documentTemplateIds && documentTemplateIds.length > 0) {
                documentTemplateIds.forEach((id: any) => {
                    this.validator.validatePositiveInteger(id);
                });
            }

            let formData = new FormData();
            formData.append(
                'documentCategoryPageTranslationId',
                documentCategoryPageTranslationId,
            );
            formData.append('documentCategoryPageId', documentCategoryPageId);
            formData.append('languageId', languageId);
            formData.append('url', url.toLowerCase());
            formData.append('metaTitle', metaTitle);
            formData.append('metaKeyword', metaKeyword);
            formData.append('metaDescription', metaDescription);
            formData.append('title', title);
            formData.append('heading', heading);
            formData.append('description', description);
            if (documentTemplateIds && documentTemplateIds.length > 0) {
                documentTemplateIds.forEach((id: any) => {
                    formData.append('documentTemplateIds[]', id);
                });
            }

            const httpResult = await this.httpService.postFormData(
                API_UPDATE_DOCUMENT_CATEGORY_PAGE_TRANSLATION,
                formData,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async get(data: any): Promise<DocumentCategoryPageTranslationResultsPojo> {
        const { documentCategoryPageId, languageId } = data;
        try {
            this.validator.validatePositiveInteger(documentCategoryPageId);
            this.validator.validatePositiveInteger(languageId);

            const httpResult = await this.httpService.get(
                GET_SINGLE_DOCUMENT_CATEGORY_PAGE_TRANSLATION(
                    documentCategoryPageId,
                    languageId,
                ),
                {},
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
                data: httpResult.data.response,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async getAllActiveDocumentTemplates(): Promise<{
        success: boolean;
        data: { documentTemplateId: number; name: string }[];
        message: null | string;
    }> {
        try {
            const httpResult = await this.httpService.get(
                GET_ALL_ACTIVE_DOCUMENT_TEMPLATES,
                {},
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
                data: httpResult.data.response,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async isUrlExists(
        url: string,
    ): Promise<{ success: boolean; message: string }> {
        try {
            this.validator.isValidString('url', url);
            const payload: any = {
                url: url.toLowerCase(),
            };

            const httpResult = await this.httpService.post(
                API_IS_URL_EXISTS,
                payload,
            );
            return {
                success: httpResult.data.isSuccess,
                message: httpResult.data.messageForUser,
            };
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }

    async delete(
        documentCategoryPageTranslationId: number,
        status: number,
    ): Promise<Boolean> {
        try {
            this.validator.validatePositiveInteger(
                documentCategoryPageTranslationId,
            );
            this.validator.validateIntegerIncludingZero(status);

            const httpResult = await this.httpService.delete(
                API_UPDATE_DOCUMENT_CATEGORY_PAGE_TRANSLATION_STATUS,
                { documentCategoryPageTranslationId, status },
            );
            return httpResult.data.isSuccess;
        } catch (e) {
            this.logger.error(e);
            throw e;
        }
    }
}
